/**
 * Receipt to lines: the owner sends a photo of a merchant receipt in chat, and
 * the reply shows what was read off it, line by line, with one tap to put the
 * whole trip on a job (docs/intelligent-chat-answers-plan.md, "Receipt to
 * lines").
 *
 * 🚨 The tap posts the SERVER'S read, never the model's. Without this block the
 * model reads the figures out of `record_receipt`'s result and types them back
 * into `record_job_costs` — every price transcribed by the thing the
 * amount-claim guard exists to catch. Here the lines, the VAT split and the
 * total are fixed on the server, stored on the block, and posted as they stand
 * to `POST /api/jobs/[id]/costs`, the same route the job's Materials card saves
 * through. The model chooses nothing on the block.
 *
 * 🚨 The VAT goes through the SAME two functions the Materials card uses
 * (`readReceiptShape`, `splitTripVat`), so a receipt added from the chat and
 * the same receipt added on the job land as the same rows. A third copy of
 * "which receipts print gross" is how the two would come to disagree (#20).
 *
 * 🚨 Never invented lines. A read with no legible lines, lines that do not add
 * up to the printed total, or more lines than a batch takes, all fall back to
 * ONE line for the whole shop at the printed total — the figure actually paid.
 * When no VAT is printed, none is derived: the line goes in gross with VAT
 * unknown, which the costs panel reports honestly as an inexact total, the
 * same refusal `record_job_costs` makes about a 20% nobody stated.
 *
 * 🚨 Which job is OFFERED, never chosen. The candidates are the jobs on the
 * receipt's day (`jobsForReceipt`). One candidate is shown as "Adding to …"
 * with Change beside it; several are listed for a tap; none means the owner
 * says which in words. The Add tap is the owner agreeing to that job.
 *
 * The block stores the attachment's ID and nothing that expires: the photo is
 * drawn through `/api/chat-attachments/<id>`, gated on whoever is looking.
 *
 * Pure and directive-free (anti-pattern #18): the panel, the Mucka Bar and the
 * oversight grader all import it.
 */
import { readReceiptShape, splitTripVat } from '@/lib/job-costs/trip-vat';

/** One line as the bubble shows it: what it was, how many, and what it came to. */
export interface ReceiptLinesRow {
  description: string;
  quantity: number;
  /** quantity × price, as printed on the receipt (gross or net per `vatIncluded`). */
  amount: number;
}

/** One line as it will be POSTED — the route's own batch-line shape. */
export interface ReceiptCostLine {
  description: string;
  quantity: number;
  /** Ex-VAT per unit when the VAT is known; the printed (gross) price when it is not. */
  unitCost: number;
  /** VAT on this line, or null when the receipt did not print one. */
  vatAmount: number | null;
}

export interface ReceiptJobOption {
  jobId: string;
  /** "08:00 · Boiler service · Dee Walsh", built server-side by `jobsForReceipt`. */
  label: string;
}

/**
 * Why the whole receipt went in as one line, when it did.
 * `unreadable`: no legible lines. `disagree`: the lines did not add up to the
 * printed total. `too-many`: more lines than one save takes.
 */
export type OneLineReason = 'unreadable' | 'disagree' | 'too-many';

export interface ReceiptLinesBlock {
  kind: 'receipt-lines';
  /** `chat_attachments.id` — the photo is drawn from it at render, never a URL. */
  attachmentId: string;
  /** How to draw the attachment: a thumbnail, or a file chip for a PDF. */
  photo: 'image' | 'document';
  vendor: string | null;
  /** YYYY-MM-DD as printed, or null when the date was not legible. */
  date: string | null;
  /** Rows shown in the bubble (capped); every posted line is in `post.lines`. */
  rows: ReceiptLinesRow[];
  /** Lines read beyond the rows shown. Never hidden silently. */
  moreRows: number;
  /** Null when the receipt was itemised and used as read. */
  oneLine: OneLineReason | null;
  /** What the read lines came to, when they disagreed with the printed total. */
  readLinesTotal: number | null;
  /** The printed total, for the `disagree` note. */
  printedTotal: number | null;
  /** True when the rows' amounts already include the VAT (the till-roll shape). */
  vatIncluded: boolean;
  /** VAT as printed, or null when the receipt did not show it. */
  vat: number | null;
  /** What goes on the job, gross: exactly what the posted lines add back to. */
  total: number;
  /** Both reads saw a total and they disagree — the owner should check the paper. */
  totalsDisagree: { first: number; second: number } | null;
  /** Jobs it might be for, most likely first. Empty without permission. */
  jobs: ReceiptJobOption[];
  /** Shown as "Adding to …" from the start. Only ever set when there is ONE candidate. */
  preselectedJobId: string | null;
  /** `record_job_costs` — without it there is no job picker and no Add button. */
  canRecord: boolean;
  /** Exactly what the Add tap sends, built here so the model never types a figure. */
  post: {
    supplier: string | null;
    incurredOn: string | null;
    chatAttachmentId: string;
    lines: ReceiptCostLine[];
  };
}

/** Rows drawn in the bubble. A forty-line docket is still added whole. */
export const RECEIPT_ROWS_SHOWN = 12;
/** Mirrors `MAX_LINES` in `src/lib/job-costs/validate.ts` (one batch). */
export const RECEIPT_MAX_LINES = 40;
/** Mirrors the route's field limits, so the save cannot be refused for length. */
const MAX_DESCRIPTION = 200;
const MAX_SUPPLIER = 120;
const MAX_UNIT_COST = 1_000_000;
const MAX_QUANTITY = 100_000;

/** A line as a vision read returned it (`extractReceiptLines`). */
export interface ReadLine {
  description: string;
  unitCost: number;
  quantity: number;
}

/** What one read of the receipt said. */
export interface ReceiptRead {
  vendor: string | null;
  date: string | null;
  total: number | null;
  vat: number | null;
  lines: ReadLine[];
}

export interface ReceiptLinesInput {
  attachmentId: string;
  photo: 'image' | 'document';
  /** `record_receipt`'s own header read (`extractReceiptFields`). */
  firstRead: Omit<ReceiptRead, 'lines'>;
  /** The line read (`extractReceiptLines`), or null when it was not made or failed. */
  lineRead: ReceiptRead | null;
  jobs: readonly { id: string; label: string }[];
  canRecord: boolean;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const isIsoDate = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
const clip = (s: string, n: number) => (s.length > n ? s.slice(0, n).trim() : s);

function validLine(l: ReadLine): boolean {
  return !!l.description.trim()
    && Number.isFinite(l.unitCost) && l.unitCost >= 0 && l.unitCost <= MAX_UNIT_COST
    && Number.isFinite(l.quantity) && l.quantity > 0 && l.quantity <= MAX_QUANTITY;
}

/**
 * The block, or null when there is nothing honest to add: no legible lines and
 * no legible total in either read.
 */
export function receiptLinesBlock(input: ReceiptLinesInput): ReceiptLinesBlock | null {
  const { firstRead, lineRead } = input;
  const vendor = lineRead?.vendor ?? firstRead.vendor ?? null;
  const date = lineRead?.date ?? firstRead.date ?? null;
  const total = lineRead?.total ?? firstRead.total ?? null;
  const vat = lineRead?.vat ?? firstRead.vat ?? null;
  const read = lineRead?.lines ?? [];

  const totalsDisagree = lineRead?.total != null && firstRead.total != null
    && Math.abs(lineRead.total - firstRead.total) > 0.01
    ? { first: firstRead.total, second: lineRead.total }
    : null;

  let oneLine: OneLineReason | null = null;
  let readLinesTotal: number | null = null;
  if (read.length === 0 || !read.every(validLine)) oneLine = 'unreadable';
  else if (read.length > RECEIPT_MAX_LINES) oneLine = 'too-many';
  else {
    const shape = readReceiptShape({ lines: read, total, vat });
    if (!shape.agrees) {
      oneLine = 'disagree';
      readLinesTotal = round2(read.reduce((s, l) => s + l.quantity * l.unitCost, 0));
    }
  }

  let rows: ReceiptLinesRow[];
  let lines: ReceiptCostLine[];
  let vatIncluded = true;
  let shownVat: number | null = vat;
  let gross: number;

  if (oneLine) {
    // ONE line for the whole shop at the printed total. Never a breakdown
    // rebuilt from the total, which would look exactly like a real read.
    if (total == null || !(total > 0) || total > MAX_UNIT_COST) return null;
    const vatOk = vat != null && vat >= 0 && vat <= total;
    shownVat = vatOk ? vat : null;
    const description = vendor ? clip(`Materials from ${vendor}`, MAX_DESCRIPTION) : 'Materials';
    rows = [{ description, quantity: 1, amount: round2(total) }];
    lines = [{
      description,
      quantity: 1,
      unitCost: vatOk ? round2(total - vat!) : round2(total),
      vatAmount: vatOk ? round2(vat!) : null,
    }];
    gross = round2(total);
  } else {
    const shape = readReceiptShape({ lines: read, total, vat });
    const split = shape.statedVat != null
      ? splitTripVat(read, { includesVat: shape.includesVat, statedVat: shape.statedVat })
      : null;
    if (split?.ok) {
      // The VAT the receipt printed, spread over its lines the way the job's
      // own form does it, so the rows add back to what was paid exactly.
      vatIncluded = shape.includesVat;
      rows = read.map(l => ({ description: clip(l.description.trim(), MAX_DESCRIPTION), quantity: l.quantity, amount: round2(l.quantity * l.unitCost) }));
      lines = read.map((l, i) => ({
        description: rows[i].description,
        quantity: l.quantity,
        unitCost: split.split.lines[i].unitCost,
        vatAmount: split.split.lines[i].vatAmount,
      }));
      gross = split.split.gross;
    } else {
      // No VAT printed (or one that cannot be true): the lines go in as
      // printed with VAT unknown. Rounded to the penny FIRST, because
      // `unit_cost` is numeric(10,2) and the job's total is recomputed from it
      // — the bubble must show the figure the job will.
      shownVat = null;
      vatIncluded = true;
      lines = read.map(l => ({
        description: clip(l.description.trim(), MAX_DESCRIPTION),
        quantity: l.quantity,
        unitCost: round2(l.unitCost),
        vatAmount: null,
      }));
      rows = lines.map(l => ({ description: l.description, quantity: l.quantity, amount: round2(l.quantity * l.unitCost) }));
      gross = round2(rows.reduce((s, r) => s + r.amount, 0));
    }
  }

  const jobs = input.canRecord ? input.jobs.map(j => ({ jobId: j.id, label: j.label })) : [];
  return {
    kind: 'receipt-lines',
    attachmentId: input.attachmentId,
    photo: input.photo,
    vendor,
    date: isIsoDate(date) ? date : null,
    rows: rows.slice(0, RECEIPT_ROWS_SHOWN),
    moreRows: Math.max(0, rows.length - RECEIPT_ROWS_SHOWN),
    oneLine,
    readLinesTotal,
    printedTotal: total,
    vatIncluded,
    vat: shownVat,
    total: gross,
    totalsDisagree,
    jobs,
    preselectedJobId: jobs.length === 1 ? jobs[0].jobId : null,
    canRecord: input.canRecord,
    post: {
      supplier: vendor ? clip(vendor, MAX_SUPPLIER) : null,
      incurredOn: isIsoDate(date) ? date : null,
      chatAttachmentId: input.attachmentId,
      lines,
    },
  };
}

/**
 * What the job's total will move by once these lines are saved, worked the way
 * `summariseJobCosts` works a line: round2(quantity × unit_cost) + vat_amount.
 * The tests hold `block.total` to it, so the button can never promise one
 * figure and put another on the job.
 */
export function recordedTotal(lines: readonly ReceiptCostLine[]): number {
  return round2(lines.reduce((s, l) => s + round2(l.quantity * l.unitCost) + (l.vatAmount ?? 0), 0));
}

/**
 * The Add tap's request, or null for a job that is not on the block. A stored
 * reply can be redrawn on any screen, so the job has to be one the server
 * offered, not whatever a caller passes.
 */
export function receiptLinesRequest(block: ReceiptLinesBlock, jobId: string): { path: string; body: Record<string, unknown> } | null {
  if (!block.canRecord || !block.jobs.some(j => j.jobId === jobId)) return null;
  return {
    path: `/api/jobs/${encodeURIComponent(jobId)}/costs`,
    body: {
      supplier: block.post.supplier,
      incurredOn: block.post.incurredOn,
      chatAttachmentId: block.post.chatAttachmentId,
      kind: 'material',
      lines: block.post.lines.map(l => ({
        description: l.description,
        quantity: l.quantity,
        unitCost: l.unitCost,
        vatAmount: l.vatAmount,
      })),
    },
  };
}

/** Said as the owner when the job on the card is not the right one. */
export const DIFFERENT_JOB_REQUEST = 'Add it to a different job';
/** Said as the owner when no job was found for the receipt's day. */
export const WHICH_JOB_REQUEST = 'Add this receipt to a job';

function pounds(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The headline the bubble shows above the lines. Built from the block alone. */
export function receiptHeadline(block: ReceiptLinesBlock): string {
  const vatWord = block.vat != null ? ' inc VAT' : '';
  if (block.oneLine === 'unreadable') return `I couldn't read the lines, so it's one line for the total: ${pounds(block.total)}${vatWord}.`;
  if (block.oneLine === 'too-many') return `That's more lines than I can add in one go, so it's one line for the total: ${pounds(block.total)}${vatWord}.`;
  if (block.oneLine === 'disagree') return `The lines didn't add up to the total, so it's one line for what you paid: ${pounds(block.total)}${vatWord}.`;
  const n = block.rows.length + block.moreRows;
  return `I read ${n} line${n === 1 ? '' : 's'}, ${pounds(block.total)}${vatWord}. Check them against the receipt.`;
}

/**
 * One line for the oversight transcript: counts, the total and the state of
 * the job choice. Nothing a customer or a supplier could be named from.
 */
export function summariseReceiptLinesBlock(block: ReceiptLinesBlock): string {
  const n = block.post.lines.length;
  const lines = block.oneLine ? `one line (${block.oneLine})` : `${n} line${n === 1 ? '' : 's'}`;
  const job = !block.canRecord
    ? 'no permission to add to a job'
    : block.preselectedJobId
      ? 'one job offered, Add button'
      : block.jobs.length
        ? `${block.jobs.length} jobs to pick from, Add button`
        : 'no job found for the day';
  const vat = block.vat == null ? ', VAT not printed' : '';
  return `[receipt-lines: ${lines}, ${pounds(block.total)}${vat}, ${job}]`;
}

/**
 * The sentences appended to `record_receipt`'s result when this block is on
 * screen. They replace the instruction to call `record_job_costs`, because the
 * button does that job with the server's own figures. The lines are still
 * given, for the one case the card cannot serve: a job that is not on it.
 */
export function receiptLinesToolNote(block: ReceiptLinesBlock): string {
  const n = block.post.lines.length;
  const vatPart = block.vat != null ? `, VAT ${pounds(block.vat)}` : ', no VAT printed';
  const read = `Read the receipt${block.vendor ? ` from ${block.vendor}` : ''}: ${block.oneLine ? 'one line for the total' : `${n} line${n === 1 ? '' : 's'}`}, total ${pounds(block.total)}${vatPart}${block.date ? `, dated ${block.date}` : ''}. NOTHING has been recorded yet.`;
  if (!block.canRecord) {
    return `${read}\nThe lines and total are on screen. This person's role cannot put costs on a job, so do not offer to and do not call record_job_costs.`;
  }
  const which = block.preselectedJobId
    ? `with the job it was most likely for ("${block.jobs[0].label}") and an Add button`
    : block.jobs.length
      ? `with ${block.jobs.length} jobs from that day to pick from and an Add button`
      : 'but no job was found for that day, so ask which job it was for';
  const listed = block.post.lines
    .map(l => `${l.description} × ${l.quantity} at ${pounds(l.unitCost)}${l.vatAmount != null ? ` + VAT ${pounds(l.vatAmount)}` : ''}`)
    .join('; ');
  return [
    read,
    `The lines, the total and the job are on screen ${which}. Do NOT call record_job_costs for this receipt: the Add button puts these exact lines on the job, with the photo. Do not read the lines out one by one; give the total and ask them to check it against the paper.`,
    `Only if they say it is for a job that is NOT on the card: find that job, then call record_job_costs with these lines exactly as listed (${listed}), supplier, purchased_on and attachment_id "${block.attachmentId}".`,
  ].join('\n');
}
